import { SKIP_ERROR_TOAST } from '../../../shared/http-context-tokens';
import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { EMPTY, Observable, defer, expand, forkJoin, of, reduce, shareReplay } from 'rxjs';
import hazardTranslations from '../translations/hazard-statements.json';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { BaseServices } from '../../kuba.services';
import { RiskFigureData, SdsEmployeeExposure, SdsExposureAccessLogRow, SdsExposureRegisterRow, SdsHazardCategory, SdsHazardStatementRecord, SdsRecord, SdsRecordFileVersion, SdsSearchSuggestion, SdsSubstitutionAssessmentRow, SdsSupplierName } from '../models/sds.model';

export interface SdsUiPictogramOption {
  id: string;
  backendId: number | null;
  lookupKey: string;
  label: string;
  ghs: string;
  svg: string;
}

export const DEFAULT_SDS_UI_PICTOGRAMS: SdsUiPictogramOption[] = [
  { id: 'explosive', backendId: null, lookupKey: 'ghs01', label: 'Explosive', ghs: 'GHS01', svg: '/assets/images/ghs/ghs-explosive.svg' },
  { id: 'flammable', backendId: null, lookupKey: 'ghs02', label: 'Flammable', ghs: 'GHS02', svg: '/assets/images/ghs/ghs-flammable.svg' },
  { id: 'oxidizer', backendId: null, lookupKey: 'ghs03', label: 'Oxidizer', ghs: 'GHS03', svg: '/assets/images/ghs/ghs-oxidizer.svg' },
  { id: 'compressed-gas', backendId: null, lookupKey: 'ghs04', label: 'Compressed Gas', ghs: 'GHS04', svg: '/assets/images/ghs/ghs-compressed-gas.svg' },
  { id: 'corrosive', backendId: null, lookupKey: 'ghs05', label: 'Corrosive', ghs: 'GHS05', svg: '/assets/images/ghs/ghs-corrosive.svg' },
  { id: 'toxic', backendId: null, lookupKey: 'ghs06', label: 'Toxic', ghs: 'GHS06', svg: '/assets/images/ghs/ghs-toxic.svg' },
  { id: 'irritant', backendId: null, lookupKey: 'ghs07', label: 'Irritant', ghs: 'GHS07', svg: '/assets/images/ghs/ghs-irritant.svg' },
  { id: 'health-hazard', backendId: null, lookupKey: 'ghs08', label: 'Health Hazard', ghs: 'GHS08', svg: '/assets/images/ghs/ghs-health-hazard.svg' },
  { id: 'environment', backendId: null, lookupKey: 'ghs09', label: 'Environment', ghs: 'GHS09', svg: '/assets/images/ghs/ghs-environment.svg' }
];

const SDS_PICTOGRAM_ALIAS_TO_UI_ID: Record<string, string> = {
  explosive: 'explosive',
  'exploding bomb': 'explosive',
  explosion: 'explosive',
  flammable: 'flammable',
  flame: 'flammable',
  oxidizer: 'oxidizer',
  oxidiser: 'oxidizer',
  oxidizing: 'oxidizer',
  oxidising: 'oxidizer',
  'flame over circle': 'oxidizer',
  'compressed gas': 'compressed-gas',
  'gas under pressure': 'compressed-gas',
  'gas cylinder': 'compressed-gas',
  corrosive: 'corrosive',
  corrosion: 'corrosive',
  toxic: 'toxic',
  'acute toxicity': 'toxic',
  'skull and crossbones': 'toxic',
  irritant: 'irritant',
  harmful: 'irritant',
  'exclamation mark': 'irritant',
  'hazardous to the ozone layer': 'irritant',
  'health hazard hazardous to the ozone layer': 'irritant',
  'health hazard': 'health-hazard',
  'serious health hazard': 'health-hazard',
  environment: 'environment',
  'hazardous to the environment': 'environment'
};

/**
 * Resolves a backend pictogram row to the UI option that carries its local SVG and its GHS code.
 *
 * The stored data does not look the way the names suggest: `LookupKey` is a blob URL
 * ("…/Pictogram/toxic.gif"), and `GHS` is a descriptive phrase ("Acute toxicity"), not a GHS
 * code. So identity is resolved by matching the normalized text of any of the three fields
 * against the alias table above, exactly as {@link SdsApiService.mapPictogramsToOptions} does
 * for the authenticated list.
 *
 * @param pictogram The stored pictogram's three text fields.
 * @returns The matching UI option, or null when nothing recognises it.
 */
export function resolveSdsPictogramUiOption(
  pictogram: { LookupKey?: string | null; GHS?: string | null; Label?: string | null }
): SdsUiPictogramOption | null {
  const normalize = (value?: string | null): string =>
    (value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  // The blob URL is the least reliable of the three, so it is tried last — but its file stem
  // ("toxic.gif" -> "toxic") is an alias too, which rescues rows with an unhelpful GHS phrase.
  const lookupStem = (pictogram.LookupKey ?? '')
    .split(/[\\/]/)
    .pop()
    ?.replace(/\.[a-z0-9]+$/i, '') ?? '';

  const candidates = [pictogram.GHS, pictogram.Label, lookupStem].map(normalize);

  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }

    // A lookup key that really is "ghs06" maps straight through.
    const direct = /^ghs\s?0?([1-9])$/.exec(candidate);
    const uiId = direct
      ? SDS_PICTOGRAM_ALIAS_TO_UI_ID[`ghs0${direct[1]}`] ?? GHS_INDEX_TO_UI_ID[direct[1]]
      : SDS_PICTOGRAM_ALIAS_TO_UI_ID[candidate];

    if (!uiId) {
      continue;
    }

    const option = DEFAULT_SDS_UI_PICTOGRAMS.find(candidateOption => candidateOption.id === uiId);
    if (option) {
      return option;
    }
  }

  return null;
}

/** GHS number to UI id, for lookup keys that really are "ghs06". */
const GHS_INDEX_TO_UI_ID: Record<string, string> = {
  '1': 'explosive',
  '2': 'flammable',
  '3': 'oxidizer',
  '4': 'compressed-gas',
  '5': 'corrosive',
  '6': 'toxic',
  '7': 'irritant',
  '8': 'health-hazard',
  '9': 'environment'
};

export interface SdsDocumentDto {
  Id?: number | null;
  ApplicationId?: number | null;
  BusinessId: number;
  Product: string;
  CASNumber: string;
  NoCasNumber?: boolean;
  Supplier?: string | null;
  Risk?: string | null;
  Exposure?: string | null;
  Control?: string | null;
  Usage?: string | null;
  Status?: string | null;
  Probability?: number | null;
  Consequence?: number | null;
  RequiresExposureTracking?: boolean | null;
  HazardCategory?: number | null;
  CreatedBy?: number | null;
  CreatedByName?: string | null;
  CreatedOn?: string | null;
  ModifiedBy?: number | null;
  ModifiedByName?: string | null;
  ModifiedOn?: string | null;
  IsDeleted?: boolean | null;
  RowVersion?: number[] | null;
  FileVersions?: SdsFileVersionDto[] | null;
  Pictograms?: SdsPictogramDto[] | null;
  ShareLinks?: SdsShareLinkDto[] | null;
  EmployeeExposures?: SdsEmployeeExposureDto[] | null;
  /** True when at least one active exposure record exists. No PII; safe for all callers. */
  HasActiveExposures?: boolean | null;
  HazardStatements?: SdsHazardStatementDto[] | null;
}

/**
 * CLP/GHS hazard statement (H / EUH / P) — returned by the reference lookup
 * endpoint and as a child of {@link SdsDocumentDto}. The English wording is
 * always populated; Norwegian (Bokmål/Nynorsk) is filled in once the seed
 * file ships official translations.
 */
export interface SdsHazardStatementDto {
  Id: number;
  Code: string;
  StatementType: 'H' | 'EUH' | 'P' | string;
  StatementEn: string;
  StatementNb?: string | null;
  StatementNn?: string | null;
  HazardGroup?: string | null;
  Subcategory?: string | null;
  IsCombined: boolean;
  SortOrder?: number | null;
}

export interface SdsEmployeeExposureDto {
  Id?: number | null;
  SDS_DocumentId?: number | null;
  BusinessId?: number | null;
  EmployeeUserId?: number | null;
  FirstName?: string | null;
  LastName?: string | null;
  Email?: string | null;
  Address?: string | null;
  NationalIdentityNumber?: string | null;
  NationalIdentityNumberMasked?: string | null;
  MedicalExamDate?: string | null;
  MedicalExamDoctorName?: string | null;
  ExposureStartDate: string | null;
  ExposureEndDate: string | null;
  RetentionExpiryDate?: string | null;
  LegalHold?: boolean;
  LastChangeReason?: string | null;
  CreatedBy?: number | null;
  CreatedAt?: string | null;
  UpdatedBy?: number | null;
  UpdatedAt?: string | null;
  DeletedOn?: string | null;
}

export interface SdsExposureRevealRequest {
  Reason: string;
}

export interface SdsExposureRevealResponse {
  ExposureId: number;
  NationalIdentityNumber?: string | null;
  RevealedAtUtc: string;
}

export interface SdsEligibleEditor {
  UserId: number;
  Name: string;
  Email?: string | null;
}

export interface SdsExposureAccessDto {
  BusinessId: number;
  AllowedEditors: SdsEligibleEditor[];
}

export interface SdsExposureAccessPermissionDto {
  BusinessId: number;
  CanAccessEmployeeExposureData: boolean;
}

export interface SdsExposureAccessUpdateRequest {
  AllowedEditorUserIds: number[];
}

export interface SdsFileVersionDto {
  Id?: number | null;
  SDS_Document_Id?: number | null;
  IsCurrent: boolean;
  CreatedBy: number;
  CreatedOn: string;
  IsDeleted?: boolean | null;
  Upload?: SdsFileVersionUploadDto | null;
}

export interface SdsFileVersionUploadDto {
  Id?: number | null;
  SDS_FileVersion_Id: number;
  Blob_URL: string;
  Name: string;
  MIME: string;
  Size: number;
  Content_Hash?: string | null;
  Storage_Provider?: string | null;
  CreatedBy: number;
  CreatedOn: string;
  IsDeleted?: boolean | null;
}

export interface SdsPictogramDto {
  Id?: number | null;
  LookupKey?: string | null;
  GHS?: string | null;
  Label?: string | null;
  Sort_Order?: string | null;
  IsDeleted?: boolean | null;
}

export interface SdsShareLinkDto {
  Id?: number | null;
  SDS_Document_Id?: number | null;
  SDS_FileVersion_Id?: number | null;
  TokenHash?: string | null;
  ShareUrl?: string | null;
  Expiry?: number | null;
  IsRevoked?: boolean | null;
  Access_Count?: number | null;
  Last_Accessed?: string | null;
  CreatedBy?: number | null;
  CreatedOn?: string | null;
  RevokedBy?: number | null;
  RevokedOn?: string | null;
  IsDeleted?: boolean | null;
}

export interface SdsCreateDocumentRequest {
  BusinessId: number;
  Product: string;
  CASNumber: string;
  NoCasNumber?: boolean;
  Supplier?: string | null;
  Risk?: string | null;
  Exposure?: string | null;
  Control?: string | null;
  Usage?: string | null;
  Status?: string | null;
  Probability?: number | null;
  Consequence?: number | null;
  RequiresExposureTracking?: boolean | null;
  HazardCategory?: number | null;
  PictogramIds?: number[] | null;
  /**
   * CLP/GHS hazard statement codes (e.g. ['H315','H319','H410']).
   * Backend rejects unknown/inactive codes with a 400 ValidationFailed.
   */
  HazardStatementCodes?: string[] | null;
  EmployeeExposures?: SdsEmployeeExposureDto[] | null;
}

export interface SdsUpdateDocumentRequest {
  Product?: string | null;
  CASNumber?: string | null;
  NoCasNumber?: boolean;
  Supplier?: string | null;
  Risk?: string | null;
  Exposure?: string | null;
  Control?: string | null;
  Usage?: string | null;
  Status?: string | null;
  Probability?: number | null;
  Consequence?: number | null;
  RequiresExposureTracking?: boolean | null;
  HazardCategory?: number | null;
  PictogramIds?: number[] | null;
  /**
   * Hazard statement codes. Omit to leave existing links untouched; pass
   * an empty array to remove all linked statements.
   */
  HazardStatementCodes?: string[] | null;
  EmployeeExposures?: SdsEmployeeExposureDto[] | null;
}

export interface SdsCreateShareLinkRequest {
  FileVersionId?: number | null;
  Expiry?: number | null;
}

export interface SdsUploadDocumentFileResponse {
  onSucess: boolean;
}

export interface SdsPublicShareLinkResponse {
  ShareUrl: string;
  ExpiresOnUtc: string;
  FileVersionId?: number | null;
  ShareLinkId?: number | null;
  TokenHash?: string | null;
}

/** Shape returned by the backend's PagedResponse<T> for the chemicals list. */
export interface SdsPagedResponse<T> {
  Items: T[];
  PageNumber: number;
  PageSize: number;
  TotalCount: number;
  TotalPages: number;
}

/** Parameters a server-paged chemicals list request accepts. */
export interface SdsListQuery {
  pageNumber: number;
  pageSize: number;
  search?: string | null;
  sortColumn?: string | null;
  sortDirection?: 'ASC' | 'DESC' | null;
  /** When true, returns archived (replaced) + deleted chemicals instead of the active list. */
  archived?: boolean | null;
}

/** A page of mapped chemical records plus the total count for the paginator. */
export interface SdsPagedRecords {
  records: SdsRecord[];
  totalCount: number;
}

/** One replacement link (original → replacement) for the history chain. */
export interface SdsReplacementLink {
  Id: number;
  OriginalDocumentId: number;
  OriginalProduct?: string | null;
  ReplacementDocumentId: number;
  ReplacementProduct?: string | null;
  ReplacedOn: string;
  ReplacedByUserId: number;
  ReplacedByName?: string | null;
  Reason?: string | null;
  IsActive: boolean;
}

/** Replacement state for one chemical plus its connected history chain. */
export interface SdsReplacementInfo {
  DocumentId: number;
  Product?: string | null;
  IsArchived: boolean;
  CanReplace: boolean;
  CurrentReplacement?: SdsReplacementLink | null;
  ReplacesActive?: SdsReplacementLink | null;
  History: SdsReplacementLink[];
}

/** A valid replacement candidate for the assign-existing search picker. */
export interface SdsReplacementCandidate {
  DocumentId: number;
  Product: string;
  CASNumber?: string | null;
  Supplier?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class SdsApiService {
  private readonly host = environment.BASE_URL_V2;
  private readonly sdsApiBaseUrl = `${this.host}/safety-data-sheets`;
  private readonly defaultShareLinkExpiryDays = 36500;

  private readonly pictogramLookupKeyToUiId: Record<string, string> = {
    ghs01: 'explosive',
    ghs02: 'flammable',
    ghs03: 'oxidizer',
    ghs04: 'compressed-gas',
    ghs05: 'corrosive',
    ghs06: 'toxic',
    ghs07: 'irritant',
    ghs08: 'health-hazard',
    ghs09: 'environment'
  };

  constructor(private http: HttpClient) {}

  private getCurrentBusinessId(): number {
    return BaseServices.BusinessId;
  }

  private withBusinessScope<T extends object>(payload?: T | null): T & { BusinessId: number } {
    return {
      ...(payload || {} as T),
      BusinessId: this.getCurrentBusinessId()
    };
  }

  getBusinessDocuments(businessId: number): Observable<SdsDocumentDto[]> {
    return this.http.get<SdsDocumentDto[]>(
      `${this.sdsApiBaseUrl}/business/${businessId}`
    );
  }

  getSupplierNames(businessId: number): Observable<SdsSupplierName[]> {
    return this.http.get<SdsSupplierName[]>(
      `${this.sdsApiBaseUrl}/business/${businessId}/suppliers`
    );
  }

  getCurrentBusinessSupplierNames(): Observable<SdsSupplierName[]> {
    return this.getSupplierNames(this.getCurrentBusinessId());
  }

  getBusinessDocumentRecords(businessId: number): Observable<SdsRecord[]> {
    return this.getBusinessDocuments(businessId).pipe(
      map(documents => documents.map(document => this.mapDocumentToRecord(document)))
    );
  }

  /** Server-paged document fetch — ordered newest-first by default, with optional search/sort. */
  getBusinessDocumentsPaged(
    businessId: number,
    query: SdsListQuery,
    risk: string | null = null
  ): Observable<SdsPagedResponse<SdsDocumentDto>> {
    let params = new HttpParams()
      .set('PageNumber', String(query.pageNumber))
      .set('PageSize', String(query.pageSize));

    if (query.search) {
      params = params.set('search', query.search);
    }
    if (query.sortColumn) {
      params = params.set('SortColumn', query.sortColumn);
    }
    if (query.sortDirection) {
      params = params.set('SortDirection', query.sortDirection);
    }
    if (query.archived) {
      params = params.set('archived', 'true');
    }
    if (risk) {
      params = params.set('risk', risk);
    }

    return this.http.get<SdsPagedResponse<SdsDocumentDto>>(
      `${this.sdsApiBaseUrl}/business/${businessId}/paged`,
      { params }
    );
  }

  /** Same as {@link getBusinessDocumentsPaged} but maps each item to the UI {@link SdsRecord}. */
  getBusinessDocumentRecordsPaged(
    businessId: number,
    query: SdsListQuery,
    risk: string | null = null
  ): Observable<SdsPagedRecords> {
    return this.getBusinessDocumentsPaged(businessId, query, risk).pipe(
      map(response => ({
        records: (response.Items || []).map(document => this.mapDocumentToRecord(document)),
        totalCount: response.TotalCount || 0
      }))
    );
  }

  searchBusinessDocumentSuggestions(
    businessId: number,
    rawQuery: string
  ): Observable<SdsSearchSuggestion[]> {
    const query = rawQuery.trim();
    if (query.length < 2) {
      return of([]);
    }

    return this.getBusinessDocuments(businessId).pipe(
      map(documents => documents
        .map(document => this.mapDocumentToSearchSuggestion(document, query))
        .filter((suggestion): suggestion is SdsSearchSuggestion => suggestion !== null)
        .sort((left, right) => right.matchScore - left.matchScore || left.productName.localeCompare(right.productName))
        .slice(0, 5))
    );
  }

  getDocument(documentId: number, includeArchived = false): Observable<SdsDocumentDto> {
    return this.http.request<SdsDocumentDto>(
      'GET',
      `${this.sdsApiBaseUrl}/${documentId}`,
      {
        body: this.withBusinessScope(),
        // Lets the Archived tab open replaced/soft-deleted documents (read-only).
        params: includeArchived ? new HttpParams().set('includeArchived', 'true') : undefined
      }
    );
  }

  getDocumentRecord(documentId: number, includeArchived = false): Observable<SdsRecord> {
    return this.getDocument(documentId, includeArchived).pipe(
      map(document => this.mapDocumentToRecord(document))
    );
  }

  createDocument(payload: SdsCreateDocumentRequest): Observable<SdsDocumentDto> {
    return this.http.post<SdsDocumentDto>(
      this.sdsApiBaseUrl,
      this.withBusinessScope(payload)
    );
  }

  updateDocument(documentId: number, payload: SdsUpdateDocumentRequest): Observable<SdsDocumentDto> {
    return this.http.put<SdsDocumentDto>(
      `${this.sdsApiBaseUrl}/${documentId}`,
      this.withBusinessScope(payload)
    );
  }

  deleteDocument(documentId: number): Observable<boolean> {
    return this.http.delete<boolean>(
      `${this.sdsApiBaseUrl}/${documentId}`,
      {
        body: this.withBusinessScope()
      }
    );
  }

  /** Archives a chemical (active list → Archived Chemicals). Reversible by design; no soft-delete. */
  archiveDocument(documentId: number): Observable<boolean> {
    return this.http.post<boolean>(
      `${this.sdsApiBaseUrl}/${documentId}/archive`,
      this.withBusinessScope()
    );
  }

  restoreDocument(documentId: number): Observable<boolean> {
    return this.http.post<boolean>(
      `${this.sdsApiBaseUrl}/${documentId}/restore`,
      this.withBusinessScope()
    );
  }

  bulkImport(businessId: number, files: File[], archiveDocumentIds: number[], documents: SdsUpdateDocumentRequest[] = []): Observable<number[]> {
    const body = new FormData();
    files.forEach(file => body.append('files', file));
    archiveDocumentIds.forEach(id => body.append('archiveDocumentIds', String(id)));
    documents.forEach((document, index) => {
      body.append(`documents[${index}].Product`, document.Product ?? '');
      body.append(`documents[${index}].CASNumber`, document.CASNumber ?? '');
    });
    return this.http.post<number[]>(`${this.sdsApiBaseUrl}/business/${businessId}/bulk-import`, body, {
      context: new HttpContext().set(SKIP_ERROR_TOAST, true)
    });
  }

  uploadDocumentFile(documentId: number, file: File): Observable<SdsUploadDocumentFileResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<SdsUploadDocumentFileResponse>(
      `${this.sdsApiBaseUrl}/${documentId}/file`,
      formData
    );
  }

  addDocumentFileFromArchive(documentId: number, archiveDocumentId: number): Observable<SdsUploadDocumentFileResponse> {
    return this.http.post<SdsUploadDocumentFileResponse>(
      `${this.sdsApiBaseUrl}/${documentId}/file-from-archive`,
      { ArchiveDocumentId: archiveDocumentId }
    );
  }

  /** Fast, flat list of the business's PDF docs in the Archive "Chemicals" folder (sorted by name). */
  getArchivePdfs(
    businessId: number, linkedOnly = false
  ): Observable<Array<{ Id: number; Name: string; Size: number | null; MimeType: string }>> {
    return this.http.get<Array<{ Id: number; Name: string; Size: number | null; MimeType: string }>>(
      `${this.sdsApiBaseUrl}/business/${businessId}/archive-pdfs`,
      { params: new HttpParams().set('linkedOnly', String(linkedOnly)) }
    );
  }

  /** Short-lived read URL for previewing a single archive PDF before confirming. */
  getArchivePdfPreviewUrl(businessId: number, archiveDocumentId: number): Observable<{ url: string }> {
    return this.http.get<{ url: string }>(
      `${this.sdsApiBaseUrl}/business/${businessId}/archive-pdfs/${archiveDocumentId}/url`
    );
  }

  getArchivePdfPreviewFile(businessId: number, archiveDocumentId: number, fileName: string, skipErrorToast = false): Observable<File> {
    return this.http.get(
      `${this.sdsApiBaseUrl}/business/${businessId}/archive-pdfs/${archiveDocumentId}/file`,
      { responseType: 'blob', context: new HttpContext().set(SKIP_ERROR_TOAST, skipErrorToast) }
    ).pipe(
      map(blob => new File(
        [blob],
        this.ensureFileName(fileName, blob.type),
        { type: blob.type || 'application/pdf' }
      ))
    );
  }

  downloadDocumentFile(fileUrl: string, fileName: string, mimeType?: string | null): Observable<File> {
    return this.http.get(fileUrl, { responseType: 'blob' }).pipe(
      map(blob => new File(
        [blob],
        this.ensureFileName(fileName, mimeType || blob.type),
        { type: blob.type || mimeType || 'application/pdf' }
      ))
    );
  }

  getDocumentFile(
    documentId: number,
    fileVersionId: number | null | undefined,
    fileName: string | null | undefined,
    mimeType?: string | null,
    download = false
  ): Observable<File> {
    return this.http.get(this.buildDocumentFileUrl(documentId, fileVersionId, download), { responseType: 'blob' }).pipe(
      map(blob => new File(
        [blob],
        this.ensureFileName(fileName, mimeType || blob.type),
        { type: blob.type || mimeType || 'application/pdf' }
      ))
    );
  }

  getPictograms(): Observable<SdsPictogramDto[]> {
    return this.http.get<SdsPictogramDto[]>(
      `${this.sdsApiBaseUrl}/pictograms`
    );
  }

  /**
   * Searches the CLP/GHS hazard-statement catalog for the p-autoComplete picker.
   * Backend matches case-insensitively across the code, English text and
   * Norwegian text, and returns at most 50 active statements.
   *
   * @param query  Free-text search term — empty/null browses the active catalog.
   * @param type   Optional filter: 'H' | 'EUH' | 'P'. Null returns all types.
   * @param skip   Offset into the stable 50-row result pages.
   */
  searchHazardStatements(
    query?: string | null,
    type?: 'H' | 'EUH' | 'P' | null,
    skip = 0
  ): Observable<SdsHazardStatementDto[]> {
    const params: { [key: string]: string } = { skip: String(skip) };
    const trimmed = (query || '').trim();
    if (trimmed.length > 0) {
      params['q'] = trimmed;
    }
    if (type) {
      params['type'] = type;
    }

    return this.http.get<SdsHazardStatementDto[]>(
      `${this.sdsApiBaseUrl}/hazard-statements`,
      { params }
    );
  }

  private hazardCatalog$?: Observable<SdsHazardStatementDto[]>;

  /** Fetch every page; an error never exposes an incomplete catalog as complete. */
  getHazardStatementCatalog(): Observable<SdsHazardStatementDto[]> {
    this.hazardCatalog$ ??= defer(() => {
      const seenIds = new Set<number>();
      const pageAt = (skip: number) => this.searchHazardStatements('', null, skip).pipe(map(page => {
        // Fail visibly if an older server ignores pagination, rather than looping forever.
        if (page.some(row => row.Id == null || seenIds.has(row.Id))) {
          throw new Error('The hazard catalog returned an overlapping page.');
        }
        page.forEach(row => seenIds.add(row.Id!));
        return page;
      }));
      return pageAt(0).pipe(
        expand((page, index) => page.length === 50 ? pageAt((index + 1) * 50) : EMPTY),
        reduce((all, page) => [...all, ...page], [] as SdsHazardStatementDto[])
      );
    }).pipe(shareReplay({ bufferSize: 1, refCount: true }));
    return this.hazardCatalog$;
  }

  hazardStatementText(statement: SdsHazardStatementDto, activeLang?: string | null): string {
    const lang = (activeLang || 'en').toLowerCase().split('-')[0];
    if (lang === 'sv' || lang === 'pl') {
      const translations: Record<string, string> = hazardTranslations[lang];
      return translations[statement.Code] || statement.StatementEn;
    }
    if (lang === 'nn') return statement.StatementNn || statement.StatementNb || statement.StatementEn;
    if (lang === 'no' || lang === 'nb') return statement.StatementNb || statement.StatementNn || statement.StatementEn;
    return statement.StatementEn;
  }

  /**
   * Resolves a list of H/EUH codes (e.g. scanned from a PDF) to their catalog
   * entries. Each code is looked up and only the exact-code match is kept;
   * unknown codes are silently dropped. Returns an empty list for no input.
   */
  resolveHazardStatementsByCodes(codes: string[]): Observable<SdsHazardStatementDto[]> {
    const unique = Array.from(
      new Set((codes || []).map(code => (code || '').trim().toUpperCase()))
    ).filter(code => code.length > 0);

    if (unique.length === 0) {
      return of([]);
    }

    return forkJoin(
      unique.map(code =>
        this.searchHazardStatements(code, null).pipe(
          map(results =>
            (results || []).find(r => (r.Code || '').toUpperCase() === code) || null
          )
        )
      )
    ).pipe(
      map(list => list.filter((s): s is SdsHazardStatementDto => s != null))
    );
  }

  /**
   * Renders a code and localized wording for picker chips and list/detail tooltips.
   * Norwegian comes from the API; Swedish/Polish use the versioned display catalog.
   * Untranslated codes fall back to English.
   */
  formatHazardStatement(
    statement: SdsHazardStatementDto,
    activeLang: string | null | undefined
  ): string {
    return `${statement.Code} — ${this.hazardStatementText(statement, activeLang)}`;
  }

  getPictogramOptions(): Observable<SdsUiPictogramOption[]> {
    return this.getPictograms().pipe(
      map(pictograms => this.mapPictogramsToOptions(pictograms))
    );
  }

  createShareLink(documentId: number, payload: SdsCreateShareLinkRequest): Observable<SdsShareLinkDto> {
    return this.http.post<SdsShareLinkDto>(
      `${this.sdsApiBaseUrl}/${documentId}/share-link`,
      payload
    );
  }

  createResolvedShareLink(
    documentId: number,
    payload?: SdsCreateShareLinkRequest | null
  ): Observable<SdsPublicShareLinkResponse> {
    const requestPayload: SdsCreateShareLinkRequest = {
      FileVersionId: payload?.FileVersionId ?? null,
      Expiry: payload?.Expiry ?? this.defaultShareLinkExpiryDays
    };

    return this.createShareLink(documentId, requestPayload).pipe(
      map(shareLink => ({
        ShareUrl: this.buildPublicSharePreviewUrl(shareLink.TokenHash, shareLink.ShareUrl),
        ExpiresOnUtc: this.calculateShareLinkExpiryIso(shareLink),
        FileVersionId: shareLink.SDS_FileVersion_Id ?? requestPayload.FileVersionId ?? null,
        ShareLinkId: shareLink.Id ?? null,
        TokenHash: shareLink.TokenHash ?? null
      }))
    );
  }

  getPublicShareFileUrl(token: string, download = false): string {
    const encodedToken = encodeURIComponent((token || '').trim());
    const downloadQuery = download ? '?download=true' : '';

    return `${this.sdsApiBaseUrl}/share/${encodedToken}${downloadQuery}`;
  }

  revokeShareLink(documentId: number, shareLinkId: number): Observable<boolean> {
    return this.http.post<boolean>(
      `${this.sdsApiBaseUrl}/${documentId}/revoke-share/${shareLinkId}`,
      this.withBusinessScope()
    );
  }

  // ===== Norwegian exposure-register endpoints =====

  /**
   * Reveals the plaintext fødselsnummer/D-number for a single exposure record.
   * Each call writes an audit log entry on the server. The reason text is required.
   */
  revealExposureNationalIdentityNumber(
    exposureId: number,
    reason: string
  ): Observable<SdsExposureRevealResponse> {
    const body: SdsExposureRevealRequest = { Reason: reason };
    return this.http.post<SdsExposureRevealResponse>(
      `${this.sdsApiBaseUrl}/exposures/${exposureId}/reveal`,
      body
    );
  }

  /** Admin: get the current allowlist of Editors permitted to see PII for a business. */
  getExposureAccess(businessId: number): Observable<SdsExposureAccessDto> {
    return this.http.get<SdsExposureAccessDto>(
      `${this.sdsApiBaseUrl}/business/${businessId}/exposure-access`
    );
  }

  /** Current user: whether SDS employee exposure data may be created/read for this business. */
  getMyExposureAccess(businessId: number): Observable<SdsExposureAccessPermissionDto> {
    return this.http.get<SdsExposureAccessPermissionDto>(
      `${this.sdsApiBaseUrl}/business/${businessId}/exposure-access/me`
    );
  }

  /** Admin: replace the allowlist atomically. Empty list = no Editor of that business sees PII. */
  setExposureAccess(
    businessId: number,
    allowedEditorUserIds: number[]
  ): Observable<SdsExposureAccessDto> {
    const body: SdsExposureAccessUpdateRequest = { AllowedEditorUserIds: allowedEditorUserIds };
    return this.http.put<SdsExposureAccessDto>(
      `${this.sdsApiBaseUrl}/business/${businessId}/exposure-access`,
      body
    );
  }

  /**
   * Returns the cross-document Exposure Register for a business — every active
   * exposure row, joined with parent SDS document context. The server strips
   * `NationalIdentityNumberMasked` for users not on the PII allowlist.
   * Each call writes a single ListView audit row server-side.
   */
  getExposureRegister(businessId: number): Observable<SdsExposureRegisterRow[]> {
    return this.http.get<SdsExposureRegisterRow[]>(
      `${this.sdsApiBaseUrl}/business/${businessId}/exposure-register`
    );
  }

  /** PII access audit trail for the Exposure Register "Access log" tab. */
  getExposureAccessLog(businessId: number): Observable<SdsExposureAccessLogRow[]> {
    return this.http.get<SdsExposureAccessLogRow[]>(
      `${this.sdsApiBaseUrl}/business/${businessId}/exposure-access-log`
    );
  }

  /**
   * Returns the Exposure Register rows for an audited export, with the UNMASKED
   * person number populated. Requires a reason; the server writes one Export
   * audit-log entry. Used to feed the XLSX/PDF/CSV register exports.
   */
  exportExposureRegisterRows(
    businessId: number,
    reason: string
  ): Observable<SdsExposureRegisterRow[]> {
    return this.http.post<SdsExposureRegisterRow[]>(
      `${this.sdsApiBaseUrl}/business/${businessId}/exposure-register/export`,
      { Reason: reason }
    );
  }

  // ===== Chemical replacement workflow =====

  /** Replacement state + history chain for a chemical. */
  getReplacementInfo(businessId: number, documentId: number): Observable<SdsReplacementInfo> {
    return this.http.get<SdsReplacementInfo>(
      `${this.sdsApiBaseUrl}/business/${businessId}/sds/${documentId}/replacement`
    );
  }

  /** Valid replacement candidates (active chemicals, no cycle, not self), filtered by query. */
  getReplacementCandidates(
    businessId: number,
    documentId: number,
    query: string
  ): Observable<SdsReplacementCandidate[]> {
    const params = new HttpParams().set('q', query || '');
    return this.http.get<SdsReplacementCandidate[]>(
      `${this.sdsApiBaseUrl}/business/${businessId}/sds/${documentId}/replacement/candidates`,
      { params }
    );
  }

  /** Assign an existing chemical as the replacement for another (archives the original). */
  assignReplacement(
    businessId: number,
    documentId: number,
    replacementDocumentId: number,
    reason: string | null
  ): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.sdsApiBaseUrl}/business/${businessId}/sds/${documentId}/replacement/assign`,
      { ReplacementDocumentId: replacementDocumentId, Reason: reason || null }
    );
  }

  // ===== Substitution assessment =====

  getSubstitutionAssessments(businessId: number, status: string): Observable<SdsSubstitutionAssessmentRow[]> {
    return this.http.get<SdsSubstitutionAssessmentRow[]>(
      `${this.sdsApiBaseUrl}/business/${businessId}/substitution-assessments`,
      { params: { status } }
    );
  }

  assessSubstitution(
    businessId: number,
    payload: { DocumentIds: number[]; AssessmentDate: string; ReminderDate: string | null; Note: string | null }
  ): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.sdsApiBaseUrl}/business/${businessId}/substitution-assessments/assess`,
      payload
    );
  }

  archiveSubstitution(businessId: number, documentId: number): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.sdsApiBaseUrl}/business/${businessId}/substitution-assessments/${documentId}/archive`,
      {}
    );
  }

  /** Admin: list the active Editors of a business — used to populate the multi-select. */
  getEligibleEditors(businessId: number): Observable<SdsEligibleEditor[]> {
    return this.http.get<SdsEligibleEditor[]>(
      `${this.sdsApiBaseUrl}/business/${businessId}/eligible-editors`
    );
  }

  /** Active employees of a business — used to auto-fill the exposure form on selection. */
  getBusinessEmployees(businessId: number): Observable<SdsEligibleEditor[]> {
    return this.http.get<SdsEligibleEditor[]>(
      `${this.sdsApiBaseUrl}/business/${businessId}/employees`
    );
  }

  mapHazardStatementsToRecord(
    statements: SdsHazardStatementDto[] | null | undefined
  ): SdsHazardStatementRecord[] {
    return [...(statements || [])]
      .sort((left, right) => (left.SortOrder ?? 0) - (right.SortOrder ?? 0))
      .map((statement, index) => ({
        Id: statement.Id,
        Code: statement.Code,
        StatementType: statement.StatementType,
        StatementEn: statement.StatementEn,
        StatementNb: statement.StatementNb,
        StatementNn: statement.StatementNn,
        IsCombined: statement.IsCombined,
        SortOrder: statement.SortOrder ?? index
      }));
  }

  /**
   * Comma-joined hazard statement codes for the compact list/badge views
   * (e.g. "H315, H319, H410"). Keeps the existing list/badge UI working
   * without having to introduce chips at every render site.
   */
  formatHazardStatementCodesForDisplay(
    statements: SdsHazardStatementDto[] | null | undefined
  ): string {
    if (!statements || statements.length === 0) {
      return '';
    }

    return [...statements]
      .sort((left, right) => (left.SortOrder ?? 0) - (right.SortOrder ?? 0))
      .map(statement => statement.Code)
      .join(', ');
  }

  mapDocumentsToRecords(documents: SdsDocumentDto[] | null | undefined): SdsRecord[] {
    const items = documents || [];
    return items.map(document => this.mapDocumentToRecord(document));
  }

  getDefaultPictogramOptions(): SdsUiPictogramOption[] {
    return DEFAULT_SDS_UI_PICTOGRAMS.map(option => ({ ...option }));
  }

  mapDocumentToRecord(document: SdsDocumentDto): SdsRecord {
    const fileVersions = this.mapFileVersionsToRecordVersions(document.FileVersions, document.ShareLinks);
    const defaultFileVersion = this.getDefaultRecordFileVersion(fileVersions);

    return this.selectFileVersion({
      Id: document.Id ?? 0,
      ProductName: document.Product,
      CASNumber: document.CASNumber || '',
      NoCasNumber: document.NoCasNumber === true,
      Supplier: document.Supplier || '',
      Pictograms: this.mapPictogramsToUiIds(document.Pictograms),
      HStatementCode: this.formatHazardStatementCodesForDisplay(document.HazardStatements),
      HazardStatements: this.mapHazardStatementsToRecord(document.HazardStatements),
      Probability: document.Probability ?? undefined,
      Consequence: document.Consequence ?? undefined,
      // Prefer the real 5×5 figure when Probability/Consequence are stored; fall
      // back to the legacy Low/Medium/High Status for older records.
      RiskFigure: (document.Probability && document.Consequence)
        ? this.buildRiskFigureFromMatrix(document.Probability, document.Consequence)
        : this.mapStatusToRiskFigure(document.Status),
      SdsFileName: '',
      SdsFileUrl: '',
      CreatedOn: this.parseDate(document.CreatedOn, document.ModifiedOn),
      // Prefer the resolved display name; fall back to the raw id only if the backend couldn't resolve it.
      EditedBy:
        document.ModifiedByName ||
        document.CreatedByName ||
        String(document.ModifiedBy || document.CreatedBy || ''),
      Description: document.Exposure || '',
      KeyRisk: document.Risk || '',
      TaskActivity: document.Usage || '',
      OtherControls: document.Control || '',
      RequiresExposureTracking: document.RequiresExposureTracking === true,
      HasActiveExposures: document.HasActiveExposures === true,
      IsDeleted: document.IsDeleted === true,
      HazardCategory: (document.HazardCategory ?? SdsHazardCategory.None) as SdsHazardCategory,
      EmployeeExposures: document.EmployeeExposures == null
        ? null
        : this.mapEmployeeExposuresToRecord(document.EmployeeExposures),
      SdsBlobUrl: undefined,
      SdsFileUploadedOn: null,
      QrShareUrl: undefined,
      QrShareExpiresOn: undefined,
      FileVersions: fileVersions,
      SelectedFileVersionId: defaultFileVersion?.Id ?? null
    }, defaultFileVersion?.Id ?? null);
  }

  mapEmployeeExposuresToDto(exposures: SdsEmployeeExposure[] | null | undefined): SdsEmployeeExposureDto[] {
    return (exposures || []).map(exposure => ({
      Id: exposure.Id ?? null,
      SDS_DocumentId: exposure.SDS_DocumentId ?? null,
      BusinessId: exposure.BusinessId ?? null,
      EmployeeUserId: exposure.EmployeeUserId ?? null,
      FirstName: exposure.FirstName || null,
      LastName: exposure.LastName || null,
      Email: exposure.Email || null,
      Address: exposure.Address || null,
      // Plaintext NIN. The server encrypts before persisting and never returns it
      // on subsequent reads — only the masked form. Omit on edits that don't change it.
      NationalIdentityNumber: exposure.NationalIdentityNumber || null,
      MedicalExamDate: this.toIsoDate(exposure.MedicalExamDate),
      MedicalExamDoctorName: exposure.MedicalExamDoctorName || null,
      ExposureStartDate: this.toIsoDate(exposure.ExposureStartDate),
      ExposureEndDate: this.toIsoDate(exposure.ExposureEndDate),
      // RetentionExpiryDate is server-computed and ignored on inbound; sending null.
      RetentionExpiryDate: null,
      LegalHold: exposure.LegalHold === true,
      LastChangeReason: exposure.LastChangeReason || null
    }));
  }

  private mapEmployeeExposuresToRecord(
    exposures: SdsEmployeeExposureDto[] | null | undefined
  ): SdsEmployeeExposure[] {
    return (exposures || []).map(exposure => ({
      Id: exposure.Id ?? null,
      SDS_DocumentId: exposure.SDS_DocumentId ?? null,
      BusinessId: exposure.BusinessId ?? null,
      EmployeeUserId: exposure.EmployeeUserId ?? null,
      FirstName: exposure.FirstName || '',
      LastName: exposure.LastName || '',
      Email: exposure.Email || '',
      Address: exposure.Address || '',
      NationalIdentityNumber: null,
      NationalIdentityNumberMasked: exposure.NationalIdentityNumberMasked || null,
      MedicalExamDate: exposure.MedicalExamDate || null,
      MedicalExamDoctorName: exposure.MedicalExamDoctorName || null,
      ExposureStartDate: exposure.ExposureStartDate || null,
      ExposureEndDate: exposure.ExposureEndDate || null,
      RetentionExpiryDate: exposure.RetentionExpiryDate || null,
      LegalHold: exposure.LegalHold === true,
      LastChangeReason: exposure.LastChangeReason || null
    }));
  }

  private toIsoDate(value: string | Date | null | undefined): string | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value.toISOString();
    }

    const timestamp = Date.parse(value);
    return Number.isNaN(timestamp) ? null : new Date(timestamp).toISOString();
  }

  selectFileVersion(record: SdsRecord, fileVersionId?: number | null): SdsRecord {
    const selectedFileVersion = this.getSelectedRecordFileVersion(record.FileVersions, fileVersionId);

    return {
      ...record,
      SelectedFileVersionId: selectedFileVersion?.Id ?? null,
      SdsFileName: selectedFileVersion?.FileName || '',
      SdsFileUrl: selectedFileVersion?.FileUrl || '',
      SdsBlobUrl: selectedFileVersion?.BlobUrl,
      SdsFileUploadedOn: selectedFileVersion?.UploadedOn ?? null,
      QrShareUrl: selectedFileVersion?.ShareUrl || '',
      QrShareExpiresOn: selectedFileVersion?.ShareExpiresOn || ''
    };
  }

  updateRecordFileVersionShare(
    record: SdsRecord,
    fileVersionId: number | null | undefined,
    shareUrl: string | null | undefined,
    shareExpiresOn: string | null | undefined,
    shareLinkId?: number | null
  ): SdsRecord {
    if (!fileVersionId) {
      return record;
    }

    const fileVersions = record.FileVersions.map(fileVersion =>
      fileVersion.Id === fileVersionId
        ? {
            ...fileVersion,
            ShareLinkId: shareLinkId ?? fileVersion.ShareLinkId ?? null,
            ShareUrl: shareUrl || '',
            ShareExpiresOn: shareExpiresOn || ''
          }
        : fileVersion
    );

    return this.selectFileVersion(
      {
        ...record,
        FileVersions: fileVersions
      },
      record.SelectedFileVersionId ?? fileVersionId
    );
  }

  mapPictogramsToOptions(pictograms: SdsPictogramDto[] | null | undefined): SdsUiPictogramOption[] {
    const fallbackOptions = this.getDefaultPictogramOptions();
    const optionsById = new Map<string, SdsUiPictogramOption>(
      fallbackOptions.map(option => [option.id, option])
    );
    const fallbackOrder = new Map<string, number>(
      fallbackOptions.map((option, index) => [option.id, index])
    );

    for (const pictogram of pictograms || []) {
      const uiId = this.mapPictogramToUiId(pictogram);
      if (!uiId) {
        continue;
      }

      const fallback = optionsById.get(uiId);
      const lookupKey = this.resolvePictogramLookupKey(pictogram, fallback);
      const ghsCode = this.toGhsCode(lookupKey, fallback?.ghs);

      optionsById.set(uiId, {
        id: uiId,
        backendId: pictogram.Id ?? fallback?.backendId ?? null,
        lookupKey: lookupKey || fallback?.lookupKey || '',
        label: this.resolvePictogramLabel(pictogram, fallback, uiId),
        ghs: ghsCode,
        svg: this.resolvePictogramSvg(pictogram, fallback)
      });
    }

    return [...optionsById.values()].sort((left, right) => {
      const leftOrder = fallbackOrder.get(left.id) ?? Number.MAX_SAFE_INTEGER;
      const rightOrder = fallbackOrder.get(right.id) ?? Number.MAX_SAFE_INTEGER;
      return leftOrder - rightOrder;
    });
  }

  private getPrimaryFileVersion(fileVersions: SdsFileVersionDto[] | null | undefined): SdsFileVersionDto | null {
    const items = fileVersions || [];
    const sorted = [...items]
      .filter(fileVersion => fileVersion.IsDeleted !== true && !!fileVersion.Upload?.Blob_URL)
      .sort((left, right) => {
        if (left.IsCurrent !== right.IsCurrent) {
          return left.IsCurrent ? -1 : 1;
        }

        return this.parseDateValue(right.Upload?.CreatedOn || right.CreatedOn) -
          this.parseDateValue(left.Upload?.CreatedOn || left.CreatedOn);
      });

    return sorted[0] ?? null;
  }

  private mapFileVersionsToRecordVersions(
    fileVersions: SdsFileVersionDto[] | null | undefined,
    shareLinks: SdsShareLinkDto[] | null | undefined
  ): SdsRecordFileVersion[] {
    const latestShareLinksByFileVersionId = this.getLatestActiveShareLinksByFileVersionId(shareLinks);

    return (fileVersions || [])
      .filter(fileVersion => fileVersion.IsDeleted !== true && !!fileVersion.Id && !!fileVersion.Upload?.Blob_URL)
      .sort((left, right) => {
        if (left.IsCurrent !== right.IsCurrent) {
          return left.IsCurrent ? -1 : 1;
        }

        return this.parseDateValue(right.Upload?.CreatedOn || right.CreatedOn) -
          this.parseDateValue(left.Upload?.CreatedOn || left.CreatedOn);
      })
      .map(fileVersion => {
        const upload = fileVersion.Upload!;
        const shareLink = latestShareLinksByFileVersionId.get(fileVersion.Id!);

        return {
          Id: fileVersion.Id!,
          IsCurrent: fileVersion.IsCurrent,
          CreatedOn: this.parseDate(fileVersion.CreatedOn, upload.CreatedOn),
          UploadedOn: this.parseDate(upload.CreatedOn, fileVersion.CreatedOn),
          FileName: upload.Name || 'SDS_Document.pdf',
          FileUrl: upload.Blob_URL,
          BlobUrl: upload.Blob_URL,
          MimeType: upload.MIME || 'application/pdf',
          Size: upload.Size || 0,
          ShareLinkId: shareLink?.Id ?? null,
          ShareUrl: this.buildPublicSharePreviewUrl(shareLink?.TokenHash, shareLink?.ShareUrl),
          ShareExpiresOn: shareLink ? this.calculateShareLinkExpiryIso(shareLink) : ''
        };
      });
  }

  private getLatestActiveShareLinksByFileVersionId(
    shareLinks: SdsShareLinkDto[] | null | undefined
  ): Map<number, SdsShareLinkDto> {
    const map = new Map<number, SdsShareLinkDto>();

    [...(shareLinks || [])]
      .filter(shareLink =>
        shareLink.IsDeleted !== true &&
        shareLink.IsRevoked !== true &&
        !!shareLink.SDS_FileVersion_Id &&
        (!!shareLink.TokenHash || !!shareLink.ShareUrl) &&
        !this.isShareLinkExpired(shareLink))
      .sort((left, right) => this.parseDateValue(right.CreatedOn) - this.parseDateValue(left.CreatedOn))
      .forEach(shareLink => {
        const fileVersionId = shareLink.SDS_FileVersion_Id!;
        if (!map.has(fileVersionId)) {
          map.set(fileVersionId, shareLink);
        }
      });

    return map;
  }

  private getDefaultRecordFileVersion(fileVersions: SdsRecordFileVersion[] | null | undefined): SdsRecordFileVersion | null {
    const items = fileVersions || [];
    return items[0] ?? null;
  }

  private getSelectedRecordFileVersion(
    fileVersions: SdsRecordFileVersion[] | null | undefined,
    fileVersionId?: number | null
  ): SdsRecordFileVersion | null {
    const items = fileVersions || [];
    if (!fileVersionId) {
      return this.getDefaultRecordFileVersion(items);
    }

    return items.find(fileVersion => fileVersion.Id === fileVersionId) ?? this.getDefaultRecordFileVersion(items);
  }

  private mapDocumentToSearchSuggestion(document: SdsDocumentDto, query: string): SdsSearchSuggestion | null {
    const upload = this.getPrimaryFileVersion(document.FileVersions)?.Upload;
    if (!document.Id || !upload?.Blob_URL) {
      return null;
    }

    const matchScore = this.calculateSearchScore(query, document, upload.Name);
    if (matchScore <= 0) {
      return null;
    }

    return {
      id: document.Id,
      productName: document.Product,
      supplier: document.Supplier || '',
      casNumber: document.CASNumber || '',
      fileName: upload.Name || 'SDS_Document.pdf',
      fileUrl: upload.Blob_URL,
      fileMime: upload.MIME || 'application/pdf',
      matchScore
    };
  }

  private calculateSearchScore(query: string, document: SdsDocumentDto, fileName?: string | null): number {
    const normalizedQuery = query.trim().toLowerCase();
    const tokens = normalizedQuery.split(/\s+/).filter(Boolean);

    return this.scoreSearchField(document.Product, normalizedQuery, tokens, 5) +
      this.scoreSearchField(document.Supplier, normalizedQuery, tokens, 3) +
      this.scoreSearchField(document.CASNumber, normalizedQuery, tokens, 4) +
      this.scoreSearchField(fileName, normalizedQuery, tokens, 2);
  }

  private scoreSearchField(
    value: string | null | undefined,
    normalizedQuery: string,
    tokens: string[],
    weight: number
  ): number {
    const normalizedValue = (value || '').trim().toLowerCase();
    if (!normalizedValue) {
      return 0;
    }

    let score = 0;
    if (normalizedValue === normalizedQuery) {
      score += weight * 100;
    } else if (normalizedValue.startsWith(normalizedQuery)) {
      score += weight * 50;
    } else if (normalizedValue.includes(normalizedQuery)) {
      score += weight * 25;
    }

    for (const token of tokens) {
      if (!token || token === normalizedQuery) {
        continue;
      }

      if (normalizedValue.startsWith(token)) {
        score += weight * 10;
        continue;
      }

      if (normalizedValue.includes(token)) {
        score += weight * 5;
      }
    }

    return score;
  }

  private calculateShareLinkExpiryIso(shareLink?: SdsShareLinkDto | null): string {
    if (!shareLink?.CreatedOn || !shareLink.Expiry) {
      return '';
    }

    const createdAt = new Date(shareLink.CreatedOn);
    if (Number.isNaN(createdAt.getTime())) {
      return '';
    }

    createdAt.setDate(createdAt.getDate() + shareLink.Expiry);
    return createdAt.toISOString();
  }

  private isShareLinkExpired(shareLink?: SdsShareLinkDto | null): boolean {
    const expiresOn = this.calculateShareLinkExpiryIso(shareLink);
    if (!expiresOn) {
      return false;
    }

    return this.parseDateValue(expiresOn) <= Date.now();
  }

  private mapPictogramsToUiIds(pictograms: SdsPictogramDto[] | null | undefined): string[] {
    const items = pictograms || [];
    return items
      .map(pictogram => this.mapPictogramToUiId(pictogram))
      .filter((id, index, values) => id.length > 0 && values.indexOf(id) === index);
  }

  private mapPictogramToUiId(pictogram: SdsPictogramDto): string {
    for (const candidate of this.collectPictogramIdentityCandidates(pictogram)) {
      if (this.isPictogramLookupKey(candidate)) {
        return this.pictogramLookupKeyToUiId[candidate] ?? candidate;
      }

      const matchedUiId = SDS_PICTOGRAM_ALIAS_TO_UI_ID[candidate];
      if (matchedUiId) {
        return matchedUiId;
      }
    }

    return this.buildCustomPictogramId(pictogram);
  }

  private resolvePictogramLookupKey(
    pictogram: SdsPictogramDto,
    fallback?: SdsUiPictogramOption
  ): string {
    const candidates = [
      ...this.collectPictogramIdentityCandidates(pictogram),
      this.normalizePictogramIdentity(fallback?.lookupKey)
    ];

    for (const candidate of candidates) {
      if (this.isPictogramLookupKey(candidate)) {
        return candidate;
      }
    }

    return (fallback?.lookupKey ?? '').trim().toLowerCase();
  }

  private isPictogramLookupKey(value: string): boolean {
    return /^ghs\d{2}$/i.test(value);
  }

  private toGhsCode(lookupKey: string, fallback?: string): string {
    if (lookupKey.startsWith('ghs')) {
      return lookupKey.toUpperCase();
    }

    return fallback || '';
  }

  private resolvePictogramLabel(
    pictogram: SdsPictogramDto,
    fallback: SdsUiPictogramOption | undefined,
    uiId: string
  ): string {
    const candidates = [fallback?.label, pictogram.Label, pictogram.GHS];

    for (const candidate of candidates) {
      const trimmedCandidate = (candidate ?? '').trim();
      if (!trimmedCandidate || this.isRemoteImageReference(trimmedCandidate)) {
        continue;
      }

      return trimmedCandidate;
    }

    return uiId;
  }

  private resolvePictogramSvg(
    pictogram: SdsPictogramDto,
    fallback?: SdsUiPictogramOption
  ): string {
    const candidates = [pictogram.LookupKey, pictogram.GHS, pictogram.Label];

    for (const candidate of candidates) {
      const backendValue = (candidate ?? '').trim();
      const normalizedBackendValue = backendValue.toLowerCase();

      if (
        normalizedBackendValue.startsWith('/assets/') ||
        normalizedBackendValue.startsWith('assets/') ||
        normalizedBackendValue.startsWith('http://') ||
        normalizedBackendValue.startsWith('https://') ||
        normalizedBackendValue.endsWith('.svg')
      ) {
        return backendValue.startsWith('assets/') ? `/${backendValue}` : backendValue;
      }
    }

    return fallback?.svg || '';
  }

  private collectPictogramIdentityCandidates(pictogram: SdsPictogramDto): string[] {
    const candidates: string[] = [];

    [pictogram.LookupKey, pictogram.GHS, pictogram.Label].forEach(rawValue => {
      this.extractPictogramIdentityCandidates(rawValue).forEach(candidate => {
        if (candidate.length > 0 && candidates.indexOf(candidate) === -1) {
          candidates.push(candidate);
        }
      });
    });

    return candidates;
  }

  private extractPictogramIdentityCandidates(value?: string | null): string[] {
    const trimmedValue = (value ?? '').trim();
    if (!trimmedValue) {
      return [];
    }

    const candidates: string[] = [];
    const pushCandidate = (candidate: string) => {
      if (candidate.length > 0 && candidates.indexOf(candidate) === -1) {
        candidates.push(candidate);
      }
    };

    pushCandidate(this.normalizePictogramIdentity(trimmedValue));

    const withoutQuery = trimmedValue.split(/[?#]/)[0];
    const segments = withoutQuery.split('/').filter(Boolean);
    if (segments.length > 0) {
      const fileName = segments[segments.length - 1];
      pushCandidate(this.normalizePictogramIdentity(fileName.replace(/\.[a-z0-9]+$/i, '')));
    }

    return candidates;
  }

  private normalizePictogramIdentity(value?: string | null): string {
    return (value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private buildCustomPictogramId(pictogram: SdsPictogramDto): string {
    if (pictogram.Id) {
      return `pictogram-${pictogram.Id}`;
    }

    const preferredCandidate = this.collectPictogramIdentityCandidates(pictogram)[0];
    return preferredCandidate ? `pictogram-${preferredCandidate.replace(/\s+/g, '-')}` : '';
  }

  private isRemoteImageReference(value: string): boolean {
    const normalizedValue = value.trim().toLowerCase();
    return normalizedValue.startsWith('http://') ||
      normalizedValue.startsWith('https://') ||
      normalizedValue.startsWith('/assets/') ||
      normalizedValue.startsWith('assets/') ||
      normalizedValue.endsWith('.svg');
  }

  /**
   * Risk figure from the 5×5 matrix (Probability × Consequence). Bands match the
   * create wizard: ≤6 Low (green), 7–14 Medium (amber), ≥15 High (red).
   */
  private buildRiskFigureFromMatrix(probability: number, consequence: number): RiskFigureData {
    const value = probability * consequence;
    if (value <= 6) {
      return { Value: value, ValueText: 'Low', BackColor: '#4ec55c' };
    }
    if (value >= 15) {
      return { Value: value, ValueText: 'High', BackColor: '#e7644f' };
    }
    return { Value: value, ValueText: 'Medium', BackColor: '#f0ac19' };
  }

  private mapStatusToRiskFigure(status: string | null | undefined): RiskFigureData {
    const normalizedStatus = (status ?? '').trim().toLowerCase();

    switch (normalizedStatus) {
      case 'high':
        return { Value: 3, ValueText: 'High', BackColor: '#e7644f' };
      case 'medium':
        return { Value: 2, ValueText: 'Medium', BackColor: '#f0ac19' };
      case 'low':
        return { Value: 1, ValueText: 'Low', BackColor: '#4ec55c' };
      default:
        return {
          Value: 0,
          ValueText: '',
          BackColor: '#9e9e9e'
        };
    }
  }

  private parseDate(primary?: string | null, fallback?: string | null): Date {
    const timestamp = this.parseDateValue(primary);
    if (timestamp > 0) {
      return new Date(timestamp);
    }

    const fallbackTimestamp = this.parseDateValue(fallback);
    if (fallbackTimestamp > 0) {
      return new Date(fallbackTimestamp);
    }

    return new Date(0);
  }

  private parseDateValue(value?: string | null): number {
    if (!value) {
      return 0;
    }

    const timestamp = Date.parse(value);
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  private ensureFileName(fileName: string | null | undefined, mimeType: string | null | undefined): string {
    const trimmedName = (fileName || '').trim();
    if (trimmedName.length > 0) {
      return trimmedName;
    }

    if ((mimeType || '').toLowerCase().includes('pdf')) {
      return 'SDS_Document.pdf';
    }

    return 'SDS_Document';
  }

  private buildDocumentFileUrl(
    documentId: number,
    fileVersionId: number | null | undefined,
    download: boolean
  ): string {
    const versionPath = fileVersionId && fileVersionId > 0
      ? `/${fileVersionId}`
      : '';
    const downloadQuery = download ? '?download=true' : '';

    return `${this.sdsApiBaseUrl}/${documentId}/file${versionPath}${downloadQuery}`;
  }

  private buildPublicSharePreviewUrl(token?: string | null, fallbackUrl?: string | null): string {
    const trimmedToken = (token || '').trim();
    if (!trimmedToken) {
      return fallbackUrl || '';
    }

    // Point the QR / share link at the backend endpoint that streams the file directly, so
    // scanning the code opens the server-served PDF instead of the SPA route on localhost.
    return `${this.sdsApiBaseUrl}/share/${encodeURIComponent(trimmedToken)}`;
  }

  private buildFrontendUrl(path: string): string {
    if (typeof window === 'undefined') {
      return path;
    }

    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${window.location.origin}${this.getFrontendBasePath()}${normalizedPath}`;
  }

  private getFrontendBasePath(): string {
    if (typeof window === 'undefined') {
      return '';
    }

    const baseElement = document.querySelector('base');
    const baseHref = baseElement?.getAttribute('href') || '/';
    const basePath = new URL(baseHref, window.location.origin).pathname.replace(/\/$/, '');
    if (basePath && basePath !== '/') {
      return basePath;
    }

    const firstPathSegment = window.location.pathname.split('/').filter(Boolean)[0];
    const hostedBasePaths = ['ik', 'ba', 'din-hms', 'ahlsell', 'ur', 'medi3', 'unipro'];
    return hostedBasePaths.includes(firstPathSegment) ? `/${firstPathSegment}` : '';
  }
}
